import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import Layout from '../../components/layout/Layout';
import Spinner from '../../components/ui/Spinner';
import { formatCurrency } from '../../utils/formatters';

const TAMANHOS = ['Berço', 'Solteiro', 'Solteiro Extra', 'Casal', 'Queen', 'King', 'Personalizado'];
const STATUS   = ['Disponível', 'Reservado', 'Vendido'];
const ESTADOS  = ['Novo', 'Usado', 'Danificado'];

const EMPTY_FORM = {
  codigo: '', nome: '', descricao: '', categoria_id: '', marca_id: '',
  tamanho: '', dimensoes: '', estado: 'Usado', status: 'Disponível',
  preco_original: '', preco_mostruario: '', observacoes: '',
};

const ProdutoForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdicao = Boolean(id);

  const [form, setForm]             = useState(EMPTY_FORM);
  const [categorias, setCategorias] = useState([]);
  const [marcas, setMarcas]         = useState([]);
  const [fotos, setFotos]           = useState([]);
  const [novasFotos, setNovasFotos] = useState([]);
  const [loading, setLoading]       = useState(isEdicao);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');

  // Vídeo
  const [videoExistente, setVideoExistente] = useState(null); // url salva no banco
  const [novoVideo, setNovoVideo]           = useState(null); // File a fazer upload
  const [removendoVideo, setRemovendoVideo] = useState(false);

  // Modo de entrada do preço mostruário: 'valor' ou 'percentual'
  const [modoDesconto, setModoDesconto] = useState('valor');
  const [percentual, setPercentual]     = useState('');

  // Calcula o preço mostruário a partir do percentual de desconto
  const precoCalculado = (() => {
    const original = parseFloat(form.preco_original);
    const pct      = parseFloat(percentual);
    if (!original || !pct || pct <= 0 || pct >= 100) return null;
    return original * (1 - pct / 100);
  })();

  // Quando muda o percentual, atualiza preco_mostruario no form automaticamente
  useEffect(() => {
    if (modoDesconto === 'percentual' && precoCalculado !== null) {
      setForm((prev) => ({ ...prev, preco_mostruario: precoCalculado.toFixed(2) }));
    }
  }, [percentual, form.preco_original, modoDesconto, precoCalculado]);

  const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '');

  useEffect(() => {
    Promise.all([api.get('/categorias'), api.get('/marcas')]).then(([c, m]) => {
      setCategorias(c.data);
      setMarcas(m.data);
    });
  }, []);

  useEffect(() => {
    if (!isEdicao) return;
    api.get(`/produtos/${id}`).then((res) => {
      const p = res.data;
      setForm({
        codigo: p.codigo || '', nome: p.nome || '', descricao: p.descricao || '',
        categoria_id: p.categoria_id || '', marca_id: p.marca_id || '',
        tamanho: p.tamanho || '', dimensoes: p.dimensoes || '',
        estado: p.estado || 'Usado', status: p.status || 'Disponível',
        preco_original: p.preco_original || '', preco_mostruario: p.preco_mostruario || '',
        observacoes: p.observacoes || '',
      });
      setFotos(p.fotos || []);
      setVideoExistente(p.video_url || null);
    }).finally(() => setLoading(false));
  }, [id, isEdicao]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleFotoAdd = (e) => {
    const files = Array.from(e.target.files);
    const total = fotos.length + novasFotos.length + files.length;
    if (total > 5) {
      setError('Limite de 5 fotos por produto.');
      return;
    }
    setNovasFotos((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const removerNovaFoto = (idx) => {
    setNovasFotos((prev) => prev.filter((_, i) => i !== idx));
  };

  const removerFotoExistente = async (fotoId) => {
    try {
      await api.delete(`/fotos/${fotoId}`);
      setFotos((prev) => prev.filter((f) => f.id !== fotoId));
    } catch (err) {
      setError('Erro ao remover foto.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      let produtoId = id;

      if (isEdicao) {
        await api.put(`/produtos/${id}`, form);
      } else {
        const res = await api.post('/produtos', form);
        produtoId = res.data.id;
      }

      // Upload das novas fotos
      if (novasFotos.length > 0) {
        const formData = new FormData();
        novasFotos.forEach((f) => formData.append('fotos', f));
        await api.post(`/produtos/${produtoId}/fotos`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      // Upload do novo vídeo
      if (novoVideo) {
        const videoData = new FormData();
        videoData.append('video', novoVideo);
        await api.post(`/produtos/${produtoId}/video`, videoData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      navigate('/admin/produtos');
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao salvar produto.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Layout><div className="flex justify-center py-20"><Spinner size="lg" /></div></Layout>;
  }

  return (
    <Layout>
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/admin/produtos')} className="btn-secondary px-3 py-2">
          <ArrowLeftIcon className="w-4 h-4" />
        </button>
        <h1 className="text-xl font-bold text-gray-900">
          {isEdicao ? 'Editar produto' : 'Novo produto'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">

        {/* Identificação */}
        <div className="card p-6 space-y-4">
          <h2 className="text-sm font-semibold text-gray-700">Identificação</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome <span className="text-red-500">*</span>
              </label>
              <input name="nome" value={form.nome} onChange={handleChange} required className="input" placeholder="Ex: Colchão Beautyrest Black" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Código</label>
              <input name="codigo" value={form.codigo} onChange={handleChange} className="input" placeholder="Ex: COL-001" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
            <textarea name="descricao" value={form.descricao} onChange={handleChange} className="input resize-none" rows={3} placeholder="Descrição detalhada do produto..." />
          </div>
        </div>

        {/* Classificação */}
        <div className="card p-6 space-y-4">
          <h2 className="text-sm font-semibold text-gray-700">Classificação</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categoria <span className="text-red-500">*</span>
              </label>
              <select name="categoria_id" value={form.categoria_id} onChange={handleChange} required className="input">
                <option value="">Selecione...</option>
                {categorias.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
              <select name="marca_id" value={form.marca_id} onChange={handleChange} className="input">
                <option value="">Selecione...</option>
                {marcas.map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tamanho</label>
              <select name="tamanho" value={form.tamanho} onChange={handleChange} className="input">
                <option value="">Selecione...</option>
                {TAMANHOS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dimensões</label>
              <input name="dimensoes" value={form.dimensoes} onChange={handleChange} className="input" placeholder="Ex: 138 x 188 x 30 cm" />
            </div>
          </div>
        </div>

        {/* Estado e Status */}
        <div className="card p-6 space-y-4">
          <h2 className="text-sm font-semibold text-gray-700">Estado e Status</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado físico</label>
              <select name="estado" value={form.estado} onChange={handleChange} className="input">
                {ESTADOS.map((e) => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select name="status" value={form.status} onChange={handleChange} className="input">
                {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Preços */}
        <div className="card p-6 space-y-4">
          <h2 className="text-sm font-semibold text-gray-700">Preços</h2>

          {/* Preço original */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Preço original (R$)</label>
            <input
              type="number" step="0.01" min="0"
              name="preco_original"
              value={form.preco_original}
              onChange={handleChange}
              className="input"
              placeholder="0,00"
            />
          </div>

          {/* Preço mostruário com toggle */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Preço mostruário</label>
              {/* Toggle valor / percentual */}
              <div className="flex rounded-lg border border-gray-300 overflow-hidden text-xs font-medium">
                <button
                  type="button"
                  onClick={() => { setModoDesconto('valor'); setPercentual(''); }}
                  className={`px-3 py-1.5 transition-colors ${
                    modoDesconto === 'valor'
                      ? 'bg-gray-900 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  R$ Valor
                </button>
                <button
                  type="button"
                  onClick={() => setModoDesconto('percentual')}
                  className={`px-3 py-1.5 transition-colors ${
                    modoDesconto === 'percentual'
                      ? 'bg-gray-900 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  % Desconto
                </button>
              </div>
            </div>

            {modoDesconto === 'valor' ? (
              <input
                type="number" step="0.01" min="0"
                name="preco_mostruario"
                value={form.preco_mostruario}
                onChange={handleChange}
                className="input"
                placeholder="0,00"
              />
            ) : (
              <div className="space-y-2">
                <div className="relative">
                  <input
                    type="number" step="0.1" min="1" max="99"
                    value={percentual}
                    onChange={(e) => setPercentual(e.target.value)}
                    className="input pr-10"
                    placeholder="Ex: 30"
                    disabled={!form.preco_original}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">%</span>
                </div>
                {!form.preco_original && (
                  <p className="text-xs text-amber-600">Informe o preço original primeiro.</p>
                )}
                {precoCalculado !== null && (
                  <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                    <span className="text-xs text-gray-500">Preço calculado:</span>
                    <span className="text-sm font-bold text-gray-900">{formatCurrency(precoCalculado)}</span>
                    <span className="text-xs text-gray-400 ml-auto">
                      {percentual}% de desconto
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Observações */}
        <div className="card p-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Observações internas</h2>
          <textarea name="observacoes" value={form.observacoes} onChange={handleChange} className="input resize-none" rows={3} placeholder="Notas internas sobre o estado, histórico, etc." />
        </div>

        {/* Fotos */}
        <div className="card p-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Fotos</h2>

          <div className="flex flex-wrap gap-3 mb-3">
            {/* Fotos existentes */}
            {fotos.map((foto) => (
              <div key={foto.id} className="relative w-24 h-24 rounded-lg overflow-hidden group">
                <img src={`${API_BASE}${foto.url}`} alt="foto" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removerFotoExistente(foto.id)}
                  className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <XMarkIcon className="w-3 h-3" />
                </button>
                {foto.ordem === 0 && (
                  <span className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs text-center py-0.5">
                    Capa
                  </span>
                )}
              </div>
            ))}

            {/* Pré-visualização de novas fotos */}
            {novasFotos.map((file, idx) => (
              <div key={idx} className="relative w-24 h-24 rounded-lg overflow-hidden group border-2 border-dashed border-gray-400">
                <img src={URL.createObjectURL(file)} alt="nova" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removerNovaFoto(idx)}
                  className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <XMarkIcon className="w-3 h-3" />
                </button>
              </div>
            ))}

            {/* Botão de adicionar */}
            {fotos.length + novasFotos.length < 5 && (
              <label className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-gray-900 hover:bg-gray-50 transition-colors">
                <PhotoIcon className="w-6 h-6 text-gray-400" />
                <span className="text-xs text-gray-400 mt-1">Adicionar</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleFotoAdd} />
              </label>
            )}
          </div>

          <p className="text-xs text-gray-400">
            {fotos.length + novasFotos.length}/5 fotos. A primeira (ordem 0) é a foto de capa.
          </p>
        </div>

        {/* Vídeo */}
        <div className="card p-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Vídeo</h2>

          {/* Vídeo existente */}
          {videoExistente && !novoVideo && (
            <div className="mb-3">
              <video
                src={`${API_BASE}${videoExistente}`}
                controls
                className="w-full rounded-lg max-h-56 bg-black"
              />
              <button
                type="button"
                disabled={removendoVideo}
                onClick={async () => {
                  setRemovendoVideo(true);
                  try {
                    await api.delete(`/produtos/${id}/video`);
                    setVideoExistente(null);
                  } catch {
                    setError('Erro ao remover vídeo.');
                  } finally {
                    setRemovendoVideo(false);
                  }
                }}
                className="mt-2 text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
                {removendoVideo ? 'Removendo...' : 'Remover vídeo'}
              </button>
            </div>
          )}

          {/* Pré-visualização do novo vídeo selecionado */}
          {novoVideo && (
            <div className="mb-3">
              <video
                src={URL.createObjectURL(novoVideo)}
                controls
                className="w-full rounded-lg max-h-56 bg-black"
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gray-500 truncate max-w-[70%]">{novoVideo.name}</span>
                <button
                  type="button"
                  onClick={() => setNovoVideo(null)}
                  className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                >
                  <XMarkIcon className="w-3.5 h-3.5" /> Remover
                </button>
              </div>
            </div>
          )}

          {/* Botão de selecionar vídeo */}
          {!novoVideo && (
            <label className="flex items-center gap-3 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-900 hover:bg-gray-50 transition-colors">
              <PhotoIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <div>
                <p className="text-sm text-gray-600 font-medium">
                  {videoExistente ? 'Substituir vídeo' : 'Selecionar vídeo'}
                </p>
                <p className="text-xs text-gray-400">MP4, MOV ou WebM — até 200 MB</p>
              </div>
              <input
                type="file"
                accept="video/mp4,video/quicktime,video/webm,video/avi"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) setNovoVideo(file);
                  e.target.value = '';
                }}
              />
            </label>
          )}
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 px-4 py-3 rounded-lg" role="alert">{error}</p>
        )}

        {/* Ações */}
        <div className="flex gap-3 justify-end pb-8">
          <button type="button" onClick={() => navigate('/admin/produtos')} className="btn-secondary">
            Cancelar
          </button>
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? 'Salvando...' : isEdicao ? 'Salvar alterações' : 'Cadastrar produto'}
          </button>
        </div>
      </form>
    </Layout>
  );
};

export default ProdutoForm;
